/**
 * Canonical cookie-header parser for the app. Used by experiments
 * (`guidCookie.ts`, `overrides.ts`), ads (`pis.ts`), and auth
 * (`google-avatar.ts`) so the splitting/trim logic doesn't drift across
 * consumers.
 *
 * The accumulator is `Object.create(null)` rather than `{}` so a cookie named
 * `__proto__` or `constructor` can't pollute the result's prototype chain.
 *
 * `decodeValues` is opt-in because some consumers (e.g. the raw `guid` value)
 * arrive un-encoded and contain `%` characters that aren't valid percent-
 * escapes. When `decodeValues: true` is set, each value is run through a
 * try/catch `decodeURIComponent` that falls back to the raw value on
 * `URIError` — a single malformed third-party cookie no longer crashes the
 * caller.
 */
type ParseOptions = {
  decodeValues?: boolean;
};

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function parseCookieHeader(
  cookieString: string,
  options: ParseOptions = {},
): Record<string, string> {
  const cookies: Record<string, string> = Object.create(null);
  if (!cookieString) return cookies;

  for (const pair of cookieString.split(';')) {
    const [rawKey, ...rest] = pair.split('=');
    const key = rawKey?.trim();
    if (!key) continue;
    const rawValue = rest.join('=').trim();
    cookies[key] = options.decodeValues ? safeDecodeURIComponent(rawValue) : rawValue;
  }
  return cookies;
}
